import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaCheck, FaTimes, FaTrophy, FaRedo, FaLanguage, FaKeyboard, FaPencilAlt, FaRandom, FaHome, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import multipleChoiceData from '../assets/multiple_choice.json';
import wordMatchingData from '../assets/word_matching.json';
import translationQuizData from '../assets/translation_quiz.json';
import fillBlankQuizData from '../assets/fill_blank_quiz.json';
import './Quiz.css';

const Quiz = () => {
  const { category = 'greetings' } = useParams();
  const navigate = useNavigate();
  const [selectedQuizType, setSelectedQuizType] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [translationInput, setTranslationInput] = useState('');
  const [blankInput, setBlankInput] = useState('');
  const [remainingPairs, setRemainingPairs] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [error, setError] = useState(null);
  const [selectedWords, setSelectedWords] = useState([]);
  const [currentPairs, setCurrentPairs] = useState([]);
  const [wordPairs, setWordPairs] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [englishPhrases, setEnglishPhrases] = useState([]);

  const quizTypes = [
    {
      id: 'multiple-choice',
      name: 'Multiple Choice',
      description: 'Choose the correct translation from multiple options',
      icon: <FaLanguage />
    },
    {
      id: 'translation',
      name: 'Translation',
      description: 'Type the correct translation of the given phrase',
      icon: <FaKeyboard />
    },
    {
      id: 'fill-blank',
      name: 'Fill in the Blank',
      description: 'Complete the Pulaar sentence with the missing word',
      icon: <FaPencilAlt />
    },
    {
      id: 'word-matching',
      name: 'Word Matching',
      description: 'Match Pulaar words with their English meanings',
      icon: <FaRandom />
    }
  ];

  const quizData = selectedQuizType === 'multiple-choice'
    ? Object.values(multipleChoiceData.multiple_choice).flat()
    : selectedQuizType === 'word-matching'
    ? Object.values(wordMatchingData.word_matching).flat()
    : selectedQuizType === 'translation'
    ? Object.values(translationQuizData.translation_quiz).flat()
    : selectedQuizType === 'fill-blank'
    ? Object.values(fillBlankQuizData.fill_blank_quiz).flat()
    : [];

  useEffect(() => {
    if (selectedQuizType === 'word-matching' && quizData.length > 0) {
      const startIndex = (currentQuestion * 4) % quizData.length;
      const pairs = quizData.slice(startIndex, startIndex + 4);
      
      const shuffledPairs = pairs.map(pair => ({
        ...pair,
        english: pair.english
      }));
      
      const englishPhrases = [...pairs].sort(() => Math.random() - 0.5).map(pair => pair.english);
      
      setWordPairs(shuffledPairs);
      setEnglishPhrases(englishPhrases);
      setMatchedPairs([]);
      setSelectedWord(null);
      setSelectedColumn(null);
    }
  }, [selectedQuizType, currentQuestion]);

  useEffect(() => {
    let timer;
    if (isTimerActive && timeLeft > 0 && selectedQuizType) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && selectedQuizType) {
      handleNextQuestion();
    }
    return () => clearInterval(timer);
  }, [timeLeft, isTimerActive, selectedQuizType]);

  const handleNextQuestion = useCallback(() => {
    if (!selectedQuizType || !quizData || quizData.length === 0) return;
    
    if (currentQuestion + 1 < Math.ceil(quizData.length / 4)) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setIsCorrect(null);
      setTimeLeft(60);
      setIsTimerActive(true);
      setTranslationInput('');
      setBlankInput('');
    } else {
      setShowScore(true);
    }
  }, [currentQuestion, selectedQuizType, quizData]);

  const handleQuizTypeSelect = (quizType) => {
    setSelectedQuizType(quizType);
    setCurrentQuestion(0);
    setScore(0);
    setShowScore(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setTimeLeft(60);
    setIsTimerActive(true);
    setSelectedMatch(null);
    setMatchedPairs([]);
    setRemainingPairs([]);
    setTranslationInput('');
    setBlankInput('');
    setWordPairs([]);
    setSelectedWord(null);
    setSelectedColumn(null);
  };

  const handleAnswerClick = (selectedIndex) => {
    if (selectedAnswer !== null || !selectedQuizType || isTransitioning) return;

    setSelectedAnswer(selectedIndex);
    setIsTimerActive(false);
    
    const isAnswerCorrect = quizData[currentQuestion].options[selectedIndex] === quizData[currentQuestion].correct_answer;
    setIsCorrect(isAnswerCorrect);
    
    if (isAnswerCorrect) {
      setScore(prev => prev + 1);
    }
  };

  const handleWordMatch = useCallback((word, column) => {
    if (!selectedWord) {
      setSelectedWord(word);
      setSelectedColumn(column);
      return;
    }

    if (selectedWord === word && selectedColumn === column) {
      setSelectedWord(null);
      setSelectedColumn(null);
      return;
    }

    if (selectedColumn === column) {
      setSelectedWord(word);
      return;
    }

    const isCorrect = wordPairs.some(pair => {
      if (selectedColumn === 'pulaar') {
        return pair.pulaar === selectedWord && pair.english === word;
      } else {
        return pair.english === selectedWord && pair.pulaar === word;
      }
    });

    if (isCorrect) {
      const newMatchedPair = {
        pulaar: selectedColumn === 'pulaar' ? selectedWord : word,
        english: selectedColumn === 'english' ? selectedWord : word
      };
      
      setMatchedPairs(prev => {
        const updated = [...prev, newMatchedPair];
        if (updated.length === wordPairs.length) {
          setTimeout(handleNextQuestion, 1000);
        }
        return updated;
      });
      
      setScore(prev => prev + 1);
    }

    setSelectedWord(null);
    setSelectedColumn(null);
  }, [selectedWord, selectedColumn, wordPairs, handleNextQuestion]);

  const handleTranslationSubmit = (e) => {
    e.preventDefault();
    if (!translationInput.trim() || isTransitioning) return;

    const correctAnswer = quizData[currentQuestion].correct_answer.toLowerCase();
    const userAnswer = translationInput.toLowerCase().trim();
    
    setIsTimerActive(false);
    setIsCorrect(userAnswer === correctAnswer);
    
    if (userAnswer === correctAnswer) {
      setScore(prev => prev + 1);
    }
  };

  const handleBlankSubmit = (e) => {
    e.preventDefault();
    if (!blankInput.trim() || isTransitioning) return;

    const correctAnswer = quizData[currentQuestion].correct_answer.toLowerCase();
    const userAnswer = blankInput.toLowerCase().trim();
    
    setIsTimerActive(false);
    setIsCorrect(userAnswer === correctAnswer);
    
    if (userAnswer === correctAnswer) {
      setScore(prev => prev + 1);
    }
  };

  const handleRestartQuiz = () => {
    setSelectedQuizType(null);
    setCurrentQuestion(0);
    setScore(0);
    setShowScore(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setTimeLeft(60);
    setIsTimerActive(true);
    setSelectedMatch(null);
    setMatchedPairs([]);
    setRemainingPairs([]);
  };

  const getProgressPercentage = () => {
    if (!quizData || quizData.length === 0) return 0;
    return ((currentQuestion + 1) / quizData.length) * 100;
  };

  const renderNextButton = () => {
    if (!quizData || quizData.length === 0) return null;
    return (
      <button 
        className="next-button"
        onClick={handleNextQuestion}
        disabled={isTransitioning}
      >
        {currentQuestion + 1 < quizData.length ? 'Next Question' : 'Finish Quiz'}
      </button>
    );
  };

  const renderWordMatchingQuiz = () => {
    if (!wordPairs.length) return null;

    return (
      <div className="word-matching-container">
        <div className="word-columns">
          <div className="word-column">
            <h3>Pulaar</h3>
            {wordPairs.map((pair, index) => {
              const isMatched = matchedPairs.some(match => match.pulaar === pair.pulaar);
              const isSelected = selectedWord === pair.pulaar && selectedColumn === 'pulaar';
              const isDisabled = isMatched || (selectedColumn === 'pulaar' && selectedWord !== pair.pulaar);
              
              return (
                <button
                  key={`pulaar-${index}`}
                  className={`word-button ${isMatched ? 'matched' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleWordMatch(pair.pulaar, 'pulaar')}
                  disabled={isDisabled}
                >
                  {pair.pulaar}
                </button>
              );
            })}
          </div>
          <div className="word-column">
            <h3>English</h3>
            {englishPhrases.map((phrase, index) => {
              const isMatched = matchedPairs.some(match => match.english === phrase);
              const isSelected = selectedWord === phrase && selectedColumn === 'english';
              const isDisabled = isMatched || (selectedColumn === 'english' && selectedWord !== phrase);
              
              return (
                <button
                  key={`english-${index}`}
                  className={`word-button ${isMatched ? 'matched' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleWordMatch(phrase, 'english')}
                  disabled={isDisabled}
                >
                  {phrase}
                </button>
              );
            })}
          </div>
        </div>
        {selectedWord && (
          <div className="matching-hint">
            Select the matching word from the {selectedColumn === 'pulaar' ? 'English' : 'Pulaar'} column
          </div>
        )}
        <div className="matching-progress">
          <p>Matched: {matchedPairs.length} of {wordPairs.length}</p>
        </div>
      </div>
    );
  };

  const renderTranslationQuiz = () => (
    <div className="translation-quiz">
      <div className="question-section">
        <h3>{quizData[currentQuestion].question}</h3>
      </div>
      <form onSubmit={handleTranslationSubmit} className="translation-form">
        <input
          type="text"
          value={translationInput}
          onChange={(e) => setTranslationInput(e.target.value)}
          placeholder="Type your translation here..."
          className="translation-input"
          disabled={isCorrect !== null}
        />
        <button 
          type="submit" 
          className="submit-translation"
          disabled={!translationInput.trim() || isCorrect !== null}
        >
          Submit
        </button>
      </form>
      <div className="feedback-section">
        {isCorrect !== null && (
          <p className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
            {isCorrect ? 'Correct!' : `Incorrect! The correct answer is: ${quizData[currentQuestion].correct_answer}`}
          </p>
        )}
      </div>
      {isCorrect !== null && renderNextButton()}
    </div>
  );

  const renderFillBlankQuiz = () => {
    const question = quizData[currentQuestion].question;
    const parts = question.split('___');
    
    return (
      <div className="fill-blank-quiz">
        <div className="question-section">
          <h3>
            {parts[0]}
            <span className="blank-space">_____</span>
            {parts[1]}
          </h3>
        </div>
        <form onSubmit={handleBlankSubmit} className="fill-blank-form">
          <input
            type="text"
            value={blankInput}
            onChange={(e) => setBlankInput(e.target.value)}
            placeholder="Type the missing word..."
            className="blank-input"
            disabled={isCorrect !== null}
            autoFocus
          />
          <button 
            type="submit" 
            className="submit-blank"
            disabled={!blankInput.trim() || isCorrect !== null}
          >
            Submit
          </button>
        </form>
        <div className="feedback-section">
          {isCorrect !== null && (
            <p className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
              {isCorrect ? 'Correct!' : `Incorrect! The correct answer is: ${quizData[currentQuestion].correct_answer}`}
            </p>
          )}
        </div>
        {isCorrect !== null && renderNextButton()}
      </div>
    );
  };

  if (!selectedQuizType) {
    return (
      <div className="quiz-page">
        <div className="quiz-container">
          <div className="quiz-type-selection">
            <h2>Choose a Quiz Type</h2>
            <div className="quiz-types-grid">
              {quizTypes.map(type => (
                <div
                  key={type.id}
                  className="quiz-type-card"
                  onClick={() => handleQuizTypeSelect(type.id)}
                >
                  <span className="quiz-type-icon">{type.icon}</span>
                  <h3>{type.name}</h3>
                  <p>{type.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="navigation-buttons">
            <button className="nav-button home-button" onClick={() => navigate('/')}>
              <FaHome /> Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showScore) {
    return (
      <div className="quiz-page">
        <div className="quiz-container">
          <div className="score-section">
            <FaTrophy className="trophy-icon" />
            <h2>Quiz Completed!</h2>
            <p>Your score: {score} out of {quizData?.length || 0}</p>
            <p>Percentage: {Math.round((score / (quizData?.length || 1)) * 100)}%</p>
            <div className="button-group">
              <button className="restart-button" onClick={() => handleQuizTypeSelect(null)}>
                <FaRedo /> Try Another Quiz
              </button>
              <button className="nav-button home-button" onClick={() => navigate('/')}>
                <FaHome /> Return Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData || quizData.length === 0) {
    return (
      <div className="quiz-page">
        <div className="quiz-container">
          <div className="error-section">
            <h2>No Quiz Data Available</h2>
            <p>Please select a different quiz type or category.</p>
            <div className="navigation-buttons">
              <button className="nav-button home-button" onClick={() => navigate('/')}>
                <FaHome /> Return Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        <div className="quiz-header">
          <h2>Pulaar Language Quiz</h2>
          <div className="navigation-buttons">
            <button className="nav-button return-button" onClick={() => handleQuizTypeSelect(null)}>
              <FaArrowLeft /> Return to Quiz Types
            </button>
            <button className="nav-button home-button" onClick={() => navigate('/')}>
              <FaHome /> Return Home
            </button>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <div className="quiz-info">
            <span>Question {currentQuestion + 1} of {quizData.length}</span>
            <span className="timer">Time: {timeLeft}s</span>
          </div>
        </div>

        {selectedQuizType === 'word-matching' ? (
          renderWordMatchingQuiz()
        ) : selectedQuizType === 'translation' ? (
          renderTranslationQuiz()
        ) : selectedQuizType === 'fill-blank' ? (
          renderFillBlankQuiz()
        ) : (
          <>
            <div className="question-section">
              <h3>{quizData[currentQuestion].question}</h3>
            </div>

            <div className="answer-section">
              {quizData[currentQuestion].options?.map((option, index) => (
                <button
                  key={index}
                  className={`answer-button ${
                    selectedAnswer === index
                      ? isCorrect
                        ? 'correct'
                        : 'incorrect'
                      : ''
                  }`}
                  onClick={() => handleAnswerClick(index)}
                  disabled={selectedAnswer !== null}
                >
                  {option}
                  {selectedAnswer === index && (
                    <span className="answer-icon">
                      {isCorrect ? <FaCheck /> : <FaTimes />}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="feedback-section">
              {selectedAnswer !== null && (
                <p className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
                  {isCorrect ? 'Correct!' : 'Incorrect!'}
                </p>
              )}
            </div>
            {selectedAnswer !== null && renderNextButton()}
          </>
        )}
      </div>
    </div>
  );
};

export default Quiz; 